/**
 * TripDayPage — Recit d'une journee de voyage.
 * Route : /carnets-de-voyage/:tripSlug/journee/:daySlug
 */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { getTripBySlug } from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";
import { ArrowLeft, ArrowRight, MapPin, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TripDayPage() {
  const { tripSlug, daySlug } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getTripBySlug(tripSlug)
      .then(async (res) => {
        setTrip(res.data);
        const currentDay = (res.data.days || []).find((d) => d.slug === daySlug);
        if (currentDay) {
          // Charger le detail complet de la journee (routes retournent tout deja mais on garde une source unique)
          try {
            const dayRes = await axios.get(`${API}/trips/${res.data.id}/days/${daySlug}`);
            setDay(dayRes.data);
          } catch {
            setDay(currentDay);
          }
        } else {
          setDay(null);
        }
      })
      .catch(() => {
        setTrip(null);
        setDay(null);
      })
      .finally(() => setLoading(false));
  }, [tripSlug, daySlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary pt-20 p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-12 w-2/3" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!trip || !day) {
    return (
      <div className="min-h-screen bg-secondary pt-24 p-6" data-testid="trip-day-not-found">
        <div className="max-w-md mx-auto text-center">
          <h1 className="font-heading text-2xl font-semibold text-primary mb-2">Journée introuvable</h1>
          <Link to={`/carnets-de-voyage/${tripSlug}`} className="text-accent underline">Retour au voyage</Link>
        </div>
      </div>
    );
  }

  const days = trip.days || [];
  const currentIdx = days.findIndex((d) => d.slug === daySlug);
  const prev = currentIdx > 0 ? days[currentIdx - 1] : null;
  const next = currentIdx < days.length - 1 ? days[currentIdx + 1] : null;

  return (
    <article className="min-h-screen bg-secondary pt-20" data-testid="trip-day-page">
      <section className="bg-white border-b border-border py-10">
        <div className="max-w-3xl mx-auto container-padding">
          <Link
            to={`/carnets-de-voyage/${trip.slug}`}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            {trip.title}
          </Link>
          <div className="text-sm text-accent font-semibold uppercase tracking-wider mb-2">
            Jour {day.day_number}
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-3">{day.stop_name}</h1>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            {day.date && (
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {new Date(day.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
              </span>
            )}
            {day.coordinates && (
              <span className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {day.coordinates.lat.toFixed(3)}, {day.coordinates.lng.toFixed(3)}
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-3xl mx-auto container-padding prose prose-lg">
          {(day.content_blocks || []).length === 0 ? (
            <p className="text-muted-foreground italic">Le récit de cette journée sera bientôt disponible.</p>
          ) : (
            (day.content_blocks || []).map((block, idx) => {
              const mediaMap = day.media_map || {};
              const resolve = (id) => mediaMap[id] || null;
              switch (block.type) {
                case "section_title":
                  return <h2 key={idx} className="font-heading text-2xl font-semibold text-primary mt-8 mb-3">{block.text}</h2>;
                case "paragraph":
                  return <p key={idx} className="text-gray-700 leading-relaxed my-4">{block.text}</p>;
                case "image": {
                  // Résolution : media_id -> URL (rotation/watermark/blur appliqués côté backend)
                  const media = resolve(block.media_id || block.photo_id);
                  const src = block.photo_url || (media && media.url);
                  if (!src) return null;
                  return (
                    <figure key={idx} className="my-6">
                      <img src={src} alt={block.caption || ""} className="w-full h-auto rounded-lg shadow" />
                      {block.caption && <figcaption className="text-sm text-gray-500 italic text-center mt-2">{block.caption}</figcaption>}
                    </figure>
                  );
                }
                case "image_gallery": {
                  const ids = block.media_ids || block.photo_ids || [];
                  const urls = (block.photo_urls && block.photo_urls.length)
                    ? block.photo_urls
                    : ids.map((id) => (resolve(id) || {}).url).filter(Boolean);
                  if (urls.length === 0) return null;
                  return (
                    <figure key={idx} className="my-6">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {urls.map((url, k) => (
                          <img key={k} src={url} alt="" className="w-full aspect-square object-cover rounded shadow" />
                        ))}
                      </div>
                      {block.caption && <figcaption className="text-sm text-gray-500 italic text-center mt-2">{block.caption}</figcaption>}
                    </figure>
                  );
                }
                case "video": {
                  const media = resolve(block.media_id);
                  const src = block.video_url || (media && media.url);
                  if (!src) return null;
                  return (
                    <figure key={idx} className="my-6">
                      <video src={src} poster={block.poster_url} controls className="w-full rounded-lg shadow" />
                      {block.caption && <figcaption className="text-sm text-gray-500 italic text-center mt-2">{block.caption}</figcaption>}
                    </figure>
                  );
                }
                case "quote":
                  return <blockquote key={idx} className="my-6 pl-6 border-l-4 border-accent italic text-gray-700">{block.text}</blockquote>;
                default:
                  return null;
              }
            })
          )}
        </div>
      </section>

      <nav className="pb-16">
        <div className="max-w-3xl mx-auto container-padding flex justify-between gap-4 border-t border-border pt-8">
          {prev ? (
            <Link
              to={`/carnets-de-voyage/${trip.slug}/journee/${prev.slug}`}
              className="flex items-center gap-2 text-primary hover:text-accent transition-colors"
              data-testid="trip-day-prev"
            >
              <ArrowLeft className="h-4 w-4" />
              <div className="text-left">
                <div className="text-xs text-muted-foreground">Jour {prev.day_number}</div>
                <div className="font-semibold">{prev.stop_name}</div>
              </div>
            </Link>
          ) : <span />}
          {next ? (
            <Link
              to={`/carnets-de-voyage/${trip.slug}/journee/${next.slug}`}
              className="flex items-center gap-2 text-primary hover:text-accent transition-colors text-right"
              data-testid="trip-day-next"
            >
              <div>
                <div className="text-xs text-muted-foreground">Jour {next.day_number}</div>
                <div className="font-semibold">{next.stop_name}</div>
              </div>
              <ArrowRight className="h-4 w-4" />
            </Link>
          ) : <span />}
        </div>
      </nav>
    </article>
  );
}
